// src/oura/components/newscan/TagInput.tsx
//
// A list typed as chips inside one field: services, rivals, other names.
// Enter or a comma adds what is typed; Backspace in an empty field removes the
// last chip; a paste splits on commas and new lines; leaving the field adds
// what is left in it. Values are trimmed and kept once, ignoring case. Every
// add and removal is said once through a polite live region, so a screen
// reader hears "Removed Dry needling" after a Backspace.
//
// Put it in a Field and pass the Field's `id` and `describedBy`. Chips can
// carry a pencil mark ("Added by you") through `marked`, or any source mark
// ("From the client's form") through `markOf`.

import { PencilIcon, XIcon, type LucideIcon } from "lucide-react";
import { useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, TAG_TONE, TYPE } from "@oura/ui";
import { TAG_INPUT } from "@oura/newscan/copy";

export interface TagInputProps {
    values: string[];
    onChange(next: string[]): void;
    /** id for the text input, from Field. */
    id?: string;
    describedBy?: string;
    /** Accessible name when there is no visible label. */
    ariaLabel?: string;
    invalid?: boolean;
    disabled?: boolean;
    placeholder?: string;
    /** Chips that get the pencil mark (by index in `values`). */
    marked?: (value: string, index: number) => boolean;
    /** The mark's words for screen readers. Default "Added by you". */
    markLabel?: string;
    /** A chip's own mark (icon and words), when chips come from different places. Wins over `marked`. */
    markOf?: (value: string, index: number) => { icon: LucideIcon; label: string } | null;
    /** No more than this many; extra adds are ignored. */
    max?: number;
    className?: string;
}

function splitTyped(raw: string): string[] {
    return raw
        .split(/[,\n\r]+/)
        .map((v) => v.trim().replace(/\s+/g, " "))
        .filter(Boolean);
}

export function TagInput({
    values,
    onChange,
    id,
    describedBy,
    ariaLabel,
    invalid,
    disabled,
    placeholder,
    marked,
    markLabel = TAG_INPUT.mark,
    markOf,
    max,
    className,
}: TagInputProps) {
    const [text, setText] = useState("");
    const [said, setSaid] = useState("");
    const input = useRef<HTMLInputElement>(null);
    /**
     * The list as this component last left it. A blur commit and a chip's remove can run back to
     * back before the parent re-renders; each reads and writes this, so the second never works on
     * the list from before the first and drops what the first added (Jack, #4285).
     */
    const latest = useRef(values);
    latest.current = values;
    const change = (next: string[]) => {
        latest.current = next;
        onChange(next);
    };

    const say = (line: string) => {
        // The same words twice in a row are still said: a zero-width change.
        setSaid((was) => (was === line ? `${line}\u200b` : line));
    };

    /** Adds every new value in `items`; returns how many were added. */
    const add = (items: string[]): number => {
        const next = [...latest.current];
        const seen = new Set(next.map((v) => v.toLowerCase()));
        const added: string[] = [];
        let dup: string | null = null;
        for (const item of items) {
            if (max !== undefined && next.length >= max) break;
            const key = item.toLowerCase();
            if (seen.has(key)) {
                dup = item;
                continue;
            }
            seen.add(key);
            next.push(item);
            added.push(item);
        }
        if (added.length > 0) {
            change(next);
            say(added.map(TAG_INPUT.added).join(". "));
        } else if (dup) {
            say(TAG_INPUT.duplicate(dup));
        }
        return added.length;
    };

    const removeAt = (i: number, refocus: boolean) => {
        const now = latest.current;
        const gone = values[i];
        // By value, not index: a commit just before may have appended to the list.
        const at = gone === undefined ? -1 : now.findIndex((v) => v === gone);
        if (at < 0) return;
        change(now.filter((_, j) => j !== at));
        say(TAG_INPUT.removed(gone));
        if (refocus) input.current?.focus();
    };

    const commit = () => {
        const items = splitTyped(text);
        if (items.length === 0) {
            setText("");
            return;
        }
        add(items);
        setText("");
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.nativeEvent.isComposing) return;
        if (e.key === "Enter" || e.key === ",") {
            // Enter adds a chip; it never submits the step from inside the list.
            if (text.trim() || e.key === ",") e.preventDefault();
            if (e.key === "Enter" && text.trim()) e.stopPropagation();
            commit();
            return;
        }
        if (e.key === "Backspace" && text === "" && values.length > 0) {
            e.preventDefault();
            removeAt(values.length - 1, false);
            return;
        }
        if (e.key === "Escape" && text) {
            // Clears what is half typed; with nothing typed, Escape is the page's.
            e.preventDefault();
            e.stopPropagation();
            setText("");
        }
    };

    const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData("text");
        if (!/[,\n\r]/.test(pasted)) return;
        e.preventDefault();
        add(splitTyped(text + pasted));
        setText("");
    };

    const full = max !== undefined && values.length >= max;

    return (
        <div
            onClick={(e) => {
                if (e.target === e.currentTarget) input.current?.focus();
            }}
            className={cn(
                "flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-xl bg-white px-2 py-1.5 ring-1 ring-inset",
                invalid ? "ring-rose-400" : "ring-black/[0.08]",
                "has-[input:focus-visible]:outline has-[input:focus-visible]:outline-2 has-[input:focus-visible]:-outline-offset-1 has-[input:focus-visible]:outline-blue-600",
                disabled && "bg-gray-50",
                className,
            )}
        >
            <ul role="list" className="list-plain contents" aria-label={ariaLabel}>
                {values.map((v, i) => {
                    const own = markOf?.(v, i) ?? null;
                    const mine = !markOf && (marked?.(v, i) ?? false);
                    const MarkIcon = own?.icon;
                    return (
                        <li
                            key={`${v.toLowerCase()}-${i}`}
                            className={cn(
                                "inline-flex h-7 max-w-full items-center gap-1 rounded-full pl-2.5 pr-0.5 font-medium ring-1 ring-inset",
                                TYPE.tm,
                                // Blue, as the product's other chips (Jovi, 20 Oct).
                                TAG_TONE.blue,
                            )}
                        >
                            {mine && (
                                <>
                                    <PencilIcon aria-hidden size={12} className="shrink-0 text-blue-400" />
                                    <span className="sr-only">{markLabel}: </span>
                                </>
                            )}
                            {own && MarkIcon && (
                                <>
                                    <span title={own.label} className="inline-flex shrink-0">
                                        <MarkIcon aria-hidden size={12} className="text-blue-400" />
                                    </span>
                                    <span className="sr-only">{own.label}: </span>
                                </>
                            )}
                            <span className="min-w-0 truncate">{v}</span>
                            <button
                                type="button"
                                disabled={disabled}
                                aria-label={TAG_INPUT.remove(v)}
                                // A pointer press on the x must not blur the input: the blur would add the
                                // half-typed text as a chip under the pointer. The text stays in the input.
                                // Keyboard users still leave the field first, which adds it, as designed.
                                onMouseDown={(e) => e.preventDefault()}
                                onPointerDown={(e) => e.preventDefault()}
                                onClick={() => removeAt(i, true)}
                                className={cn(
                                    "relative grid h-6 w-6 shrink-0 place-items-center rounded-full text-blue-400 hover:bg-white hover:text-blue-700",
                                    "after:absolute after:-inset-2.5 after:content-[''] motion-safe:transition-colors",
                                    FOCUS_RING,
                                )}
                            >
                                <XIcon aria-hidden size={12} strokeWidth={2.5} />
                            </button>
                        </li>
                    );
                })}
            </ul>
            <input
                ref={input}
                id={id}
                type="text"
                value={text}
                disabled={disabled || full}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                aria-label={id ? undefined : ariaLabel}
                // Shown whenever the field is empty, chips or not: it is where the next one goes.
                placeholder={placeholder}
                autoComplete="off"
                enterKeyHint="enter"
                onChange={(e) => {
                    const v = e.target.value;
                    // A comma typed on a phone keyboard arrives here, not as a key.
                    if (v.includes(",")) {
                        add(splitTyped(v));
                        setText("");
                        return;
                    }
                    setText(v);
                }}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onBlur={commit}
                className={cn(
                    // Wide enough to read the placeholder in full: next to chips it wraps to its own line.
                    !text && placeholder ? "min-w-[12rem]" : "min-w-[3rem]",
                    "h-7 max-w-full flex-1 bg-transparent px-1 text-base text-gray-900 outline-none placeholder:text-gray-400 sm:text-sm",
                )}
            />
            <span aria-live="polite" className="sr-only">
                {said}
            </span>
        </div>
    );
}
